/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import React, { FunctionComponent, Suspense, lazy, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import type { Ace } from "ace-builds";
import { ConditionData, ConditionFieldProps, SCRIPT } from "@advapacs/common";
import { BlockUI } from "../../BlockUI";

const AceScriptEditor = lazy(() => import("./AceScriptEditor"));

const Script: FunctionComponent<ConditionFieldProps> = ({
  disabled,
  parent,
  id,
  errors,
  condition,
  isHasScope = true,
  onValueChangeHandler,
  onHandleScriptModal,
  isDark,
}: ConditionFieldProps) => {
  const { t } = useTranslation("rule");
  const [editing, setEditing] = useState(false);
  const [editorValue, setEditorValue] = useState<string | undefined>(
    condition?.script
  );
  const [annotations, setAnnotations] = useState<Ace.Annotation[]>([]);

  const dispatchNewValue = (con: ConditionData) => {
    onValueChangeHandler(
      {
        type: SCRIPT,
        scope: isHasScope ? "conditions" : undefined,
        id,
        script: con,
      },
      parent
    );
  };

  const updateValue = () => {
    dispatchNewValue({ ...condition, script: editorValue, annotations });
    setEditing(false);
    if (onHandleScriptModal) onHandleScriptModal(false);
  };

  const validateScript = (annos: Ace.Annotation[]) => {
    setAnnotations(annos);
  };
  const hasError = () => {
    return (
      errors.find((item) => item.id === id && item.type === SCRIPT) !==
      undefined
    );
  };

  return (
    <>
      <div className="row">
        <div className="col">
          <Button
            variant="secondary"
            className="add-condition"
            onClick={() => {
              setEditing(true);
              if (onHandleScriptModal) onHandleScriptModal(true);
            }}
          >
            {t("view_script")}
          </Button>
          <Modal show={editing} centered dialogClassName="full-modal-dialog">
            <Modal.Header onClick={() => setEditing(false)}>
              <Modal.Title>{t("script_editor")}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="">
              <Suspense fallback={<BlockUI isBlocked>{null}</BlockUI>}>
                <AceScriptEditor
                  id={id}
                  defaultValue={condition?.script}
                  disabled={disabled}
                  isDark={isDark}
                  onChange={(value) => setEditorValue(value)}
                  onValidate={validateScript}
                />
              </Suspense>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="light"
                onClick={() => {
                  setEditing(false);
                  if (onHandleScriptModal) onHandleScriptModal(false);
                }}
              >
                {t("close")}
              </Button>
              {!disabled && (
                <Button variant="primary" onClick={updateValue}>
                  {t("save")}
                </Button>
              )}
            </Modal.Footer>
          </Modal>
          {hasError() && !condition?.script && (
            <label
              className="validation-invalid-label"
              htmlFor={`${id}_script`}
            >
              {t("script_required")}
            </label>
          )}
          {hasError() &&
            condition?.annotations &&
            condition.annotations.length > 0 &&
            condition.annotations.filter((ann) => ann.type === "error").length >
              0 && (
              <label
                className="validation-invalid-label"
                htmlFor={`${id}_script`}
              >
                {t("rule:script_invalid")}
              </label>
            )}
        </div>
      </div>
    </>
  );
};

export default Script;
